import React, { useEffect, useRef } from 'react';

import { EditAccessCodesDialog } from 'components/EditAccessCodesDialog/EditAccessCodesDialog';
import type { TableFilter } from 'components/GMetrixTable/components/toolbar/components';
import { AssignedAccessCode, Transaction, TransactionApi } from 'apis/TransactionApi';
import { EXPORT_RECORD_LIMIT } from 'components/GMetrixTable/GMetrixTable';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { useTransactionDetails } from 'pages/access_codes/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { SortDescriptor } from 'utils/dataQuery';
import { GMetrixTable } from 'components';
import { useJoyride } from 'app/joyride';
import { TransactionType } from 'types';
import { useSuperState } from 'hooks';

interface AccessCodeTabProps {
    contentTiedToInactiveOrder: boolean;
    handleDuplicateTransaction: (dataItem: Transaction) => Promise<void>;
}

export function AccessCodeTab(props: AccessCodeTabProps) {

    const { contentTiedToInactiveOrder, handleDuplicateTransaction } = props;

    const [state, setState] = useSuperState({
        accessCodes: [] as AssignedAccessCode[],
        editAccessCodesDialogShow: {
            accessCodeObj: undefined,
            show: false
        },
        loadingGridDataCode: false as boolean,
        paginationSkip: 0,
        paginationTake: 15,
        redeemedStatus: { id: 1, name: 'All' },
        searchTerm: '',
        serverTotalCount: 0,
        sortDir: '',
        sortField: ''
    });
    const { accessCodes, editAccessCodesDialogShow, loadingGridDataCode, paginationSkip, paginationTake, redeemedStatus,
        searchTerm, serverTotalCount, sortDir, sortField } = state;

    const { pageContext, update } = useTransactionDetails();
    const { formatMessage } = useIntl();
    const { loadCurrentTour } = useJoyride();
    const { createToast } = useNotification();

    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isInitialSearchRef = useRef(true);

    useEffect(() => {

        loadCurrentTour('CodesTab');

    }, []);

    useEffect(() => {

        if (pageContext?.Id) {

            fetchAccessCodesPaged();

        }

    }, [pageContext?.Id]);

    // Debounce search term changes (3s delay)
    useEffect(() => {

        if (!pageContext?.Id) return;

        // Skip the first render so we don't double-fetch on mount
        if (isInitialSearchRef.current) {

            isInitialSearchRef.current = false;
            return;

        }

        if (searchDebounceRef.current) {

            clearTimeout(searchDebounceRef.current);

        }
        searchDebounceRef.current = setTimeout(() => {

            setState({ paginationSkip: 0 });
            fetchAccessCodesPaged(0, paginationTake, sortField, sortDir, searchTerm, redeemedStatus.id);

        }, 3000);

        return () => {

            if (searchDebounceRef.current) {

                clearTimeout(searchDebounceRef.current);

            }

        };

    }, [searchTerm]);

    const fetchAccessCodesPaged = async (
        skip = paginationSkip,
        take = paginationTake,
        sField = sortField,
        sDir = sortDir,
        search = searchTerm,
        redeemed = redeemedStatus.id
    ) => {

        if (!pageContext?.Id) return;
        setState({ loadingGridDataCode: true });
        const page = Math.floor(skip / take) + 1;

        try {

            const result = await TransactionApi.GetAccessCodesByTransactionIdPaged(
                pageContext.Id,
                page,
                take,
                sField,
                sDir,
                search,
                redeemed,
                pageContext.Data.transactionDetails?.PartnerId ?? null
            );
            setState({ accessCodes: result.Items, serverTotalCount: result.TotalCount });

        } catch (e) {

            console.error(e);
            const message = typeof e === 'string' ? e : (e as any)?.message || (e as any)?.Message || '';
            if (message.toLowerCase().includes('timed out') || message.toLowerCase().includes('timeout')) {

                createToast(formatMessage({ id: 'the_request_timed_out_please_try_again.93e77e8d', defaultMessage: 'The request timed out. Please try again.' }), 'danger');

            } else {

                createToast(formatMessage({ id: 'failed_to_load_access_codes_please_try_a.70d10e7e', defaultMessage: 'Failed to load access codes. Please try again.' }), 'danger');

            }

        } finally {

            setState({ loadingGridDataCode: false });

        }

    };

    const handleServerPageChange = (skip: number, take: number) => {

        setState({ paginationSkip: skip, paginationTake: take });
        fetchAccessCodesPaged(skip, take, sortField, sortDir, searchTerm, redeemedStatus.id);

    };

    // Export fetch: first EXPORT_RECORD_LIMIT rows for the current filters/sort,
    // fetched fresh without touching the table's page state.
    const handleExportAllData = async () => {

        if (!pageContext?.Id) return [];
        const result = await TransactionApi.GetAccessCodesByTransactionIdPaged(
            pageContext.Id, 1, EXPORT_RECORD_LIMIT, sortField, sortDir, searchTerm,
            redeemedStatus.id, pageContext.Data.transactionDetails?.PartnerId ?? null
        );
        return result.Items;

    };

    const handleServerSortChange = (sort: SortDescriptor[]) => {

        const newSortField = sort.length > 0 ? sort[0].field : '';
        const newSortDir = sort.length > 0 ? (sort[0].dir ?? '') : '';
        setState({ paginationSkip: 0, sortDir: newSortDir, sortField: newSortField });
        fetchAccessCodesPaged(0, paginationTake, newSortField, newSortDir, searchTerm, redeemedStatus.id);

    };

    const handleRedeemedStatusChange = (status: { id: number; name: string }) => {

        setState({ paginationSkip: 0, redeemedStatus: status });
        fetchAccessCodesPaged(0, paginationTake, sortField, sortDir, searchTerm, status.id);

    };

    const setGlobalAccessCodeList = (accessCodeList: AssignedAccessCode[]) => {

        if (pageContext && pageContext.Id) {

            let defaultAccessCode: undefined | string = undefined;
            if (accessCodeList && accessCodeList.length === 1) {

                defaultAccessCode = accessCodeList[0].AccessCodeTitle;

            } else if (
                accessCodeList &&
                accessCodeList.length > 1 &&
                pageContext.Data.transactionDetails?.TransactionType === TransactionType.Assignment
            ) {

                const activeCodes = accessCodeList.filter(code => code.Active);
                if (activeCodes.length > 0) {

                    defaultAccessCode = activeCodes[0].AccessCodeTitle;

                }

            }

            update({
                accessCodes: accessCodeList,
                defaultAccessCode
            });

            // Refresh paginated view after edit
            fetchAccessCodesPaged();

        }

    };

    const filters: TableFilter[] = [
        {
            label: formatMessage({ id: 'redemption_status.e0ef92ec', defaultMessage: 'Redemption Status' }),
            options: [
                {
                    checked: redeemedStatus.id === 1,
                    label: formatMessage({ id: 'all.6feedc20', defaultMessage: 'All' }),
                    onSelect: () => handleRedeemedStatusChange({ id: 1, name: 'All' }),
                    value: 1
                },
                {
                    checked: redeemedStatus.id === 2,
                    label: formatMessage({ id: 'redeemed.03edbd00', defaultMessage: 'Redeemed' }),
                    onSelect: () => handleRedeemedStatusChange({ id: 2, name: 'Redeemed' }),
                    value: 2
                },
                {
                    checked: redeemedStatus.id === 3,
                    label: formatMessage({ id: 'not_redeemed.7da0a752', defaultMessage: 'Not Redeemed' }),
                    onSelect: () => handleRedeemedStatusChange({ id: 3, name: 'Not Redeemed' }),
                    value: 3
                }
            ],
            type: 'radio'
        }
    ];

    return (
        <>
            {pageContext && (
                <>
                    <EditAccessCodesDialog
                        editAccessCodeObj={editAccessCodesDialogShow.accessCodeObj}
                        handleDuplicateTransaction={handleDuplicateTransaction}
                        onCancel={() => setState({ editAccessCodesDialogShow: { accessCodeObj: undefined, show: false } })}
                        onSuccess={() => setState({ editAccessCodesDialogShow: { accessCodeObj: undefined, show: false } })}
                        setGlobalAccessCodeList={setGlobalAccessCodeList}
                        show={editAccessCodesDialogShow.show}
                        transactionId={pageContext.Id}
                    />

                    <GMetrixTable
                        allowExport
                        serverSidePagination
                        toggleColumns
                        columnData={[
                            {
                                cell: (props: any) => (
                                    <td>
                                        {props.dataItem.AccessCodeTitle}
                                    </td>
                                ),
                                field: 'AccessCodeTitle',
                                title: formatMessage({ id: 'code.cc651d9c', defaultMessage: 'Code' })
                            },
                            { field: 'Active', title: formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' }) },
                            { field: 'Redeemed', title: formatMessage({ id: 'redeemed.03edbd00', defaultMessage: 'Redeemed' }) },
                            { field: 'Remaining', title: formatMessage({ id: 'remaining.a4b6ea18', defaultMessage: 'Remaining', description: 'Gridview header' }) },
                            {
                                field: 'ExpirationDateString',
                                title: formatMessage({ id: 'redemption_expiration_date.d1e3b886', defaultMessage: 'Redemption Expiration Date' })
                            },
                            {
                                cell: (props: any) => (
                                    <td>
                                        <GMetrixButton
                                            onClick={() => {

                                                navigator.clipboard.writeText(
                                                    props.dataItem.AccessCodeTitle
                                                );
                                                createToast(formatMessage({ id: 'code_was_copied_to_clipboard.3ca3c48b', defaultMessage: 'Code was copied to clipboard' }), 'success');

                                            }}
                                            className="m-1"
                                            iconClass="fa-regular fa-clone"
                                            variant="outline-primary"
                                        >
                                            <FormattedMessage defaultMessage="Copy" id="copy.fa37e351" />
                                        </GMetrixButton>
                                    </td>
                                ),
                                title: formatMessage({ id: 'actions.61fd1d29', defaultMessage: 'Actions' }),
                                width: '140px'
                            }
                        ]}
                        bottomBarTableName={formatMessage({ id: 'codes.348234f6', defaultMessage: 'Codes', description: 'Formulario' })}
                        excelExportFileName={formatMessage({ id: 'transactionaccesscodes.634db984', defaultMessage: 'TransactionAccessCodes' })}
                        exportContext="Report"
                        filters={filters}
                        loading={loadingGridDataCode}
                        onExportAllData={handleExportAllData}
                        onServerPageChange={handleServerPageChange}
                        onServerSortChange={handleServerSortChange}
                        rowData={accessCodes}
                        searchPlaceholder={formatMessage({ id: 'search_by_name_code_or_status.1d8fc0de', defaultMessage: 'Search by name, code, or status...' })}
                        searchTerm={searchTerm}
                        serverTotalCount={serverTotalCount}
                        setSearchTerm={val => setState({ searchTerm: val })}
                    />

                </>
            )}
        </>
    );

}
